import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Info } from 'lucide-react';
import { useCreateIncident, useCategories, useCalculateDegree } from '../hooks/useIncidents';
import { useEmployeesHCM } from '@/features/people-core/hooks/useHCM';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { DEGREE_LABELS } from '../types';
import { toast } from '@/shared/lib/toast';

export function CreateIncidentPage() {
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [financialLoss, setFinancialLoss] = useState('');

  const { data: employees = [], isLoading: loadingEmployees } = useEmployeesHCM({ status: 'ACTIVE' });
  const { data: categories = [], isLoading: loadingCategories } = useCategories();
  const { data: degreeCalculation } = useCalculateDegree(employeeId || undefined);
  const createIncident = useCreateIncident();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!employeeId || !categoryId || !description.trim()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      await createIncident.mutateAsync({
        employeeId,
        categoryId,
        description: description.trim(),
        financialLoss: financialLoss ? parseFloat(financialLoss) : undefined,
      });

      toast.success('Incidente registrado com sucesso');
      navigate('/incident-management');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao registrar incidente';
      toast.error(message);
    }
  };

  const isLoading = loadingEmployees || loadingCategories;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/incident-management')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Registrar Incidente</h1>
          <p className="text-muted-foreground">Preencha os dados da ocorrência disciplinar</p>
        </div>
      </div>

      {/* Informações Importantes */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1 text-sm text-blue-900">
              <p>• O sistema calcula automaticamente o grau sugerido com base no histórico</p>
              <p>• Prejuízos até R$500 são cobertos pelo crédito do colaborador</p>
              <p>• Documentos de advertência são gerados automaticamente após aprovação</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulário Principal */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Dados do Incidente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Colaborador */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Colaborador <span className="text-red-500">*</span>
                </label>
                <Select
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  options={[
                    { value: '', label: 'Selecione o colaborador...' },
                    ...employees.map((employee) => ({
                      value: employee.id,
                      label: `${employee.user?.name} - ${employee.user?.email}`,
                    })),
                  ]}
                  required
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Categoria <span className="text-red-500">*</span>
                </label>
                <Select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  options={[
                    { value: '', label: 'Selecione a categoria...' },
                    ...categories.map((category) => ({
                      value: category.id,
                      label: category.name,
                    })),
                  ]}
                  required
                />
                {categories.find((c) => c.id === categoryId)?.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {categories.find((c) => c.id === categoryId)?.description}
                  </p>
                )}
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Descrição detalhada <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  placeholder="Descreva detalhadamente o ocorrido, incluindo data, hora, local e circunstâncias..."
                  required
                  minLength={10}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Mínimo 10 caracteres ({description.length} caracteres)
                </p>
              </div>

              {/* Prejuízo Financeiro */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <DollarSign className="w-4 h-4" />
                  Prejuízo Financeiro (opcional)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={financialLoss}
                  onChange={(e) => setFinancialLoss(e.target.value)}
                  placeholder="0.00"
                  helperText="Informe o valor do prejuízo causado, se houver"
                />
              </div>
            </CardContent>
          </Card>

          {/* Preview e Informações */}
          <div className="space-y-6">
            {/* Cálculo de Grau */}
            {degreeCalculation && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Previsão de Grau</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Grau Atual</p>
                    <Badge>{DEGREE_LABELS[degreeCalculation.currentDegree]}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Grau Sugerido</p>
                    <Badge variant="warning">{DEGREE_LABELS[degreeCalculation.suggestedDegree]}</Badge>
                  </div>
                  {degreeCalculation.lastIncidentDate && (
                    <div>
                      <p className="text-sm text-muted-foreground">Último Incidente</p>
                      <p className="text-sm text-foreground">
                        Há {degreeCalculation.daysSinceLastIncident} dias
                      </p>
                    </div>
                  )}
                  <div className="pt-3 border-t">
                    <p className="text-sm font-medium mb-1">Motivo</p>
                    <p className="text-sm text-muted-foreground">{degreeCalculation.reason}</p>
                  </div>
                  {degreeCalculation.isReincidence && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <p className="text-sm text-orange-800">
                        ⚠️ Reincidência detectada (menos de 90 dias)
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Impacto Financeiro Preview */}
            {financialLoss && parseFloat(financialLoss) > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Impacto Financeiro</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Prejuízo Total</p>
                    <p className="text-lg font-bold text-red-600">
                      R$ {parseFloat(financialLoss).toFixed(2)}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    A distribuição entre crédito, empresa e colaborador será calculada automaticamente
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={createIncident.isPending || !employeeId || !categoryId || !description.trim()}
              >
                {createIncident.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Registrando...
                  </>
                ) : (
                  'Registrar Incidente'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/incident-management')}
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
